import axios from 'axios';
import { BrowserAuthErrorCodes, InteractionRequiredAuthError } from '@azure/msal-browser';
import authStore from '../store/auth';
import { msalInstance } from '../App';
import { loginRequest } from './msal';
import toast from 'react-hot-toast';
import { getVersionHeaders, fetchVersionInfo } from './versioning';
import {
  auditStampRequest,
  auditHandleResponse,
  auditHandleError
} from '../services/audit/interceptors';
import { extractApiError } from '../utils';
import { reportError, debug } from '../services/logger';
import { installRequestLog, recordRequest } from '../services/requestLog';

export const httpRequest = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: 600000
});

export const agentApi = axios.create({
  baseURL: import.meta.env.VITE_AGENT_API_URL,
  timeout: 600000
});

// One shared refresh at a time. When 20 queries all hit 401 after inactivity we
// used to fire 20 parallel acquireTokenSilent calls (and 20 failure toasts on the
// unhappy path). This dedupes them onto a single in-flight promise.
let refreshPromise = null;
// The scheduled pre-expiry refresh. Cleared on every schedule call so a later
// refresh's expiresOn wins over an older one.
let preExpiryTimer = null;
// Two minutes before expiry — enough slack to cover a slow refresh round-trip
// without cutting close to a real request racing the tick.
const PRE_EXPIRY_MS = 2 * 60 * 1000;
// Never sit on a token for more than this without a refresh, even if the token
// response arrives without an expiresOn (defensive default; MSAL's SPA tokens
// are typically ~1h, so this stays within lifetime).
const FALLBACK_REFRESH_MS = 50 * 60 * 1000;
const NETWORK_RETRY_MS = 30 * 1000;
const NETWORK_RETRY_LIMIT = 3;
const NETWORK_ERROR_CODES = [
  BrowserAuthErrorCodes.postRequestFailed,
  BrowserAuthErrorCodes.noNetworkConnectivity
];

const clearPreExpiryTimer = () => {
  if (preExpiryTimer) {
    clearTimeout(preExpiryTimer);
    preExpiryTimer = null;
  }
};

const requiresInteractiveLogin = err =>
  err instanceof InteractionRequiredAuthError ||
  err?.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout;

// Bounce through the interactive login when the silent refresh cannot recover on
// its own (refresh token dead, MSAL cache cleared, tenant policy asks for a
// fresh interaction). Preserves the current URL as state so the user lands back
// where they started.
const redirectToInteractiveLogin = () => {
  try {
    const state = encodeURIComponent(window.location.href);
    // acquireTokenRedirect navigates away on success but returns a rejected
    // promise (e.g. interaction_in_progress) when it can't — must be caught
    // here or it surfaces as an unhandled rejection in Sentry.
    msalInstance.acquireTokenRedirect({ ...loginRequest, state }).catch(redirectErr => {
      reportError('api.interactiveLoginRedirect', redirectErr);
    });
  } catch (redirectErr) {
    reportError('api.interactiveLoginRedirect', redirectErr);
  }
};

export const refreshAzureToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const username = authStore.user?.username;

      if (!msalInstance.getActiveAccount() && username) {
        const account = msalInstance.getAccountByUsername(username);
        if (account) msalInstance.setActiveAccount(account);
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest
      });

      // Roll the pre-expiry timer forward off this refresh's own expiresOn so
      // idle tabs keep themselves alive without waiting for a request to 401.
      schedulePreExpiryRefresh(tokenResponse);
      return tokenResponse;
    } catch (err) {
      // An iframe timeout can require interactive sign-in even without an interaction-required error.
      if (requiresInteractiveLogin(err)) {
        redirectToInteractiveLogin();
      }
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const runBackgroundRefresh = (attempt = 0) => {
  preExpiryTimer = null;
  refreshAzureToken()
    .then(fresh => {
      if (fresh?.accessToken) {
        authStore.authorize(fresh.account, fresh.accessToken);
      }
    })
    .catch(err => {
      if (NETWORK_ERROR_CODES.includes(err?.errorCode)) {
        if (attempt < NETWORK_RETRY_LIMIT && preExpiryTimer === null) {
          preExpiryTimer = setTimeout(() => runBackgroundRefresh(attempt + 1), NETWORK_RETRY_MS);
        }
        return;
      }
      if (!requiresInteractiveLogin(err)) {
        reportError('api.backgroundTokenRefresh', err);
      }
    });
};

// Pre-expiry silent refresh. Called after every successful acquire — the timer
// clears any previous one, so the newest expiresOn always wins. Two-minute
// safety margin so we finish before the token dies mid-request.
export const schedulePreExpiryRefresh = tokenResponse => {
  clearPreExpiryTimer();
  const expiresOn = tokenResponse?.expiresOn;
  const expiresAt = expiresOn instanceof Date ? expiresOn.getTime() : Number(expiresOn) || 0;
  const delay = expiresAt
    ? Math.max(expiresAt - Date.now() - PRE_EXPIRY_MS, 0)
    : FALLBACK_REFRESH_MS;
  preExpiryTimer = setTimeout(runBackgroundRefresh, delay);
};

const buildErrorData = (rawData, message, status, fallbackCode) => {
  if (
    rawData &&
    typeof rawData === 'object' &&
    !(rawData instanceof Blob) &&
    !Array.isArray(rawData)
  ) {
    return {
      detail: rawData.detail ?? rawData.message ?? message,
      message,
      error_code: rawData.error_code ?? fallbackCode,
      ...rawData
    };
  }

  if (Array.isArray(rawData)) {
    return {
      message,
      detail: rawData,
      details: rawData,
      error_code: fallbackCode
    };
  }

  if (typeof rawData === 'string' && rawData) {
    return {
      message: rawData,
      detail: rawData,
      error_code: fallbackCode
    };
  }

  return {
    message,
    detail: message,
    error_code: fallbackCode || (status ? `HTTP_${status}` : 'NETWORK_ERROR')
  };
};

const normalizeRejectedError = (error, rawData, message, status) => {
  const fallbackCode =
    error?.code === 'ECONNABORTED' ? 'TIMEOUT_ERROR' : status ? `HTTP_${status}` : 'NETWORK_ERROR';
  const normalizedData = buildErrorData(rawData, message, status, fallbackCode);

  error.data = normalizedData;
  if (error.response) {
    error.response.data = normalizedData;
  }

  return error;
};

const redirectToAccessDenied = () => {
  if (window.location.pathname !== '/access-denied') {
    window.location.replace('/access-denied');
  }
};

const getRequestPath = config => {
  const requestUrl = config?.url || '';

  try {
    return new URL(requestUrl, config?.baseURL || window.location.origin).pathname.replace(
      /^\/+/,
      ''
    );
  } catch {
    return requestUrl.split('?')[0].replace(/^\/+/, '');
  }
};

const shouldRedirectToAccessDenied = request => {
  if (request?.skipAccessDeniedRedirect) {
    return false;
  }

  return getRequestPath(request) === 'api/user-groups/user';
};

const errorHandler = async error => {
  const status = error?.response?.status;
  const originalRequest = error.config;
  let responseData = error?.response?.data;

  // When responseType is 'blob', error.response.data is a Blob — parse it to extract the detail
  let message = responseData?.detail;
  if (message === undefined && responseData instanceof Blob) {
    try {
      responseData = JSON.parse(await responseData.text());
      message = responseData?.message || responseData?.detail;
    } catch {
      // ignore — message stays undefined if blob parse fails
    }
  }

  // Also pick up new structured envelope { message } field
  if (!message) {
    message = responseData?.message;
  }

  const normalizedMessage =
    message || extractApiError(error, 'An error occurred. Please try again.');
  const normalizedError = normalizeRejectedError(error, responseData, normalizedMessage, status);

  if (status === 403 && shouldRedirectToAccessDenied(originalRequest)) {
    if (window.location.pathname !== '/access-denied') {
      toast.error(message || 'Access denied');
    }
    redirectToAccessDenied();

    return Promise.reject(normalizedError);
  }

  if (status === 401 && !originalRequest._retry) {
    originalRequest._retry = true; // Avoid infinite loops

    try {
      const tokenResponse = await refreshAzureToken();

      // Retry the failed request with the new token
      originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;

      // Acquire token silent success
      let accessToken = tokenResponse.accessToken;

      authStore.authorize(tokenResponse.account, accessToken);

      // The retried request will go back through responseHandler — audit fires there
      return httpRequest(originalRequest);
    } catch (refreshError) {
      // Interactive fallback was requested; redirect failures are reported separately.
      if (!requiresInteractiveLogin(refreshError)) {
        reportError('api.tokenRefresh', refreshError, { url: originalRequest?.url });
      }
      auditHandleError(originalRequest, 401, 'Authentication failed');
      return Promise.reject(
        normalizeRejectedError(
          refreshError,
          refreshError?.response?.data,
          'Authentication failed',
          401
        )
      );
    }
  }

  auditHandleError(originalRequest, status ?? 0, normalizedMessage);

  return Promise.reject(normalizedError);
};

const agentErrorHandler = async error => {
  const status = error?.response?.status;
  const originalRequest = error.config;

  if (status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const tokenResponse = await refreshAzureToken();
      originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return agentApi(originalRequest);
    } catch (refreshError) {
      if (!requiresInteractiveLogin(refreshError)) {
        reportError('api.agentTokenRefresh', refreshError, { url: originalRequest?.url });
      }
      return Promise.reject(
        normalizeRejectedError(
          refreshError,
          refreshError?.response?.data,
          'Authentication failed',
          401
        )
      );
    }
  }

  return Promise.reject(
    normalizeRejectedError(
      error,
      error?.response?.data,
      extractApiError(error, 'An error occurred. Please try again.'),
      status
    )
  );
};

if (import.meta.env.DEV) installRequestLog();

const requestHandler = async config => {
  if (import.meta.env.DEV) recordRequest(config);
  await auditStampRequest(config);

  const token = authStore.token;
  const workflowId = config?.headers?.workflow_id || authStore.workflowId;

  // Get active company ID from localStorage (set by CompanySwitcher)
  const activeCompanyId = localStorage.getItem('activeCompanyId');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (workflowId) {
    config.headers.workflow_id = workflowId;
  }

  // Send active company ID as header for per-tab company context
  if (activeCompanyId) {
    config.headers['X-Active-Company-Id'] = activeCompanyId;
  }

  // Add version headers to all requests (don't overwrite per-request overrides)
  const versionHeaders = getVersionHeaders();
  Object.keys(versionHeaders).forEach(key => {
    if (config.headers[key] === undefined || config.headers[key] === null) {
      config.headers[key] = versionHeaders[key];
    }
  });

  return config;
};

const responseHandler = response => {
  // On first request, fetch version info from backend
  if (!responseHandler.versionFetched) {
    responseHandler.versionFetched = true;
    fetchVersionInfo()
      .then(versionInfo => {
        if (versionInfo) {
          debug('api.versionInfo', {
            schemaVersion: versionInfo.schema_version,
            supportedVersions: versionInfo.supported_schema_versions
          });
        }
      })
      .catch(err => {
        reportError('api.versionInfo', err);
      });
  }

  auditHandleResponse(response);

  return response.data;
};

httpRequest.interceptors.request.use(requestHandler);
httpRequest.interceptors.response.use(responseHandler, errorHandler);

agentApi.interceptors.request.use(requestHandler);
agentApi.interceptors.response.use(responseHandler, agentErrorHandler);
